import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import IconTooltipButton from '../../../../../button/IconTooltipButton';
import { useEffect, useMemo, useState } from 'react';
import { Chip, Divider, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import AutocompleteField from '../../../../../FormControl/input/AutocompleteField';
import DialogConfirmCancel from '../../../../../dialog/DialogConfirm';
import FormDataCode from './FormDataCode';
import { onFindCode } from '../../../utils';

const DialogAddCode = ({ show, onCancel, onConfirm, assessments, currentCurriculum }) => {
  const [selectedCode, setSelectedCode] = useState(null);
  const [errors, setErrors] = useState({});

  const onChange = (value) => {
    const code = assessments?.find(
      (item) => item.code === value.code && item.curriculumId === value.curriculumId
    );
    setSelectedCode({
      ...code,
      assessmentData: code
    });
  };

  const onApply = () => {
    if (selectedCode) {
      onConfirm(selectedCode.assessmentData);
      onCancel();
    } else {
      setErrors({ codeSelected: 'Please select a code' });
    }
  };

  return (
    <DialogConfirmCancel show={show} title='Add code' onCancel={onCancel} onConfirm={onApply} maxWidth='xs'>
      <div className='grid grid-cols-4 items-center gap-4 py-4 px-4'>
        <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
          Code
        </Typography>
        <div className='col-span-3'>
          <AutocompleteField
            id='code-assessment-area'
            name='codeSelected'
            placeholder='Select a code'
            value={selectedCode}
            onChange={(e) => onChange(e)}
            options={assessments || []}
            groupBy={(option) => option?.curriculum}
          />
          {errors?.codeSelected && (
            <FormHelperText error={!!errors?.codeSelected}>{errors?.codeSelected}</FormHelperText>
          )}
        </div>
        {selectedCode?.assessmentData && (
          <FormDataCode currentCurriculum={currentCurriculum} form={selectedCode?.assessmentData || {}} />
        )}
      </div>
    </DialogConfirmCancel>
  );
};
const FormPreviousPR = ({ form = {}, onChange, type, curriculum }) => {
  const previousPeriodicalReport = useSelector(
    (state) => state?.periodicalReport?.form.previousPeriodicalReport || null
  );

  const [exampleCode, setExampleCode] = useState([]);
  const [modal, setModal] = useState(false);

  const onRemove = (code) => {
    const newData = form?.previousAssessmentData?.filter((item) => item._id !== code._id) || [];
    onChange('previousAssessmentData', newData);
  };

  useEffect(() => {
    setExampleCode(onFindCode(form?.assessmentData, previousPeriodicalReport?.assessmentAreas || []));
  }, [previousPeriodicalReport, form?.assessmentData]);

  const optionsAssessmentAreas = useMemo(() => {
    const formCode = form?.previousAssessmentData?.map((item) => ({
      code: item.code,
      curriculumId: item.curriculumId
    }));

    return (
      previousPeriodicalReport.assessmentAreas
        ?.filter((item) => {
          const find = formCode?.find(
            (value) => item?.assessmentData?.code === value.code && item.curriculumId === value.curriculumId
          );
          return !find;
        })
        ?.map((item) => ({
          value: item?.assessmentData?.code,
          label: `${item?.assessmentData?.code} - ${item?.assessmentData?.name}`,
          curriculum: curriculum?.find((curr) => curr._id === item.curriculumId)?.name || '',
          ...item.assessmentData
        }))
        ?.sort((a, b) => {
          if (a.curriculum < b.curriculum) return -1;
          if (a.curriculum > b.curriculum) return 1;
          if (a.value < b.value) return -1;
          if (a.value > b.value) return 1;
          return 0;
        }) || []
    );
  }, [previousPeriodicalReport?.assessmentAreas, form?.previousAssessmentData]);

  if (!previousPeriodicalReport) {
    return <></>;
  }
  return (
    <div className='py-2'>
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex justify-between items-center gap-1 h-8'>
        <Typography>Codes related to the previous periodical report </Typography>
      </Box>
      <Box className='px-2 py-2'>
        <Box className='flex items-center gap-2'>
          <div>
            <Typography variant='subtitle2' className='font-bold'>
              Code by default
            </Typography>
            <Typography variant='body2' className='opacity-50'>
              These codes are related to the previous periodical report and will be used by default.
              Considering the elements of the evaluation area structure, according to the selected curriculum,
              area and subarea.
            </Typography>
          </div>

          <div className='flex items-center gap-2'>
            {exampleCode?.length > 0 ? (
              exampleCode.map((code, index) => (
                <Chip
                  key={index}
                  variant='body2'
                  label={`${code.assessmentData.code} - ${code.assessmentData.name}`}
                  className='cursor-pointer'
                />
              ))
            ) : (
              <Chip variant='body2' className='opacity-50' label='No codes available' />
            )}
          </div>
        </Box>
      </Box>
      <Divider />

      <Box className='px-2 py-2'>
        <div className='flex items-center gap-2'>
          <Typography variant='subtitle2' className='font-bold'>
            New codes associated with the previous periodical report
          </Typography>
          {type !== 'preview' && (
            <IconTooltipButton
              title='Add code'
              icon={'plus'}
              color='primary'
              size='small'
              onClick={() => setModal(true)}
            />
          )}
        </div>
        <TableContainer>
          <Table>
            <TableBody>
              {form?.previousAssessmentData?.map((code, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography>
                      {code?.code} - {code?.name}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    {type !== 'preview' && (
                      <IconTooltipButton
                        title='Remove code'
                        icon={'trash'}
                        size='small'
                        onClick={() => onRemove(code)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {modal && (
        <DialogAddCode
          show={modal}
          onCancel={() => setModal(false)}
          onConfirm={(value) => {
            const newData = form?.previousAssessmentData || [];
            newData.push(value);
            onChange('previousAssessmentData', newData);
          }}
          assessments={optionsAssessmentAreas}
        />
      )}
    </div>
  );
};

export default FormPreviousPR;
